import { Box, Flex, Text, Heading, Separator } from '@radix-ui/themes'; // Added Heading and Separator
import React, { useEffect, useRef } from 'react';
import { formatRelativeTime } from '../../utils/helpers.js'; // Assuming this path is correct

// Theme colors (consistent with ProfilePopup)
const tealGradientStart = '#2dd4bf'; // Brighter teal
const tealGradientEnd = '#10b981';   // Darker green/teal

// CSS Styles for the NotificationDropdown
const notificationStyles = `
  .notification-dropdown-container {
    background: rgba(255, 255, 255, 0.9); /* Slightly more opaque for text readability */
    backdrop-filter: blur(8px);
    border: 1px solid rgba(226, 232, 240, 0.7); /* Softer border */
    border-radius: 16px; /* Consistent with profile sections */
    box-shadow: 0 10px 35px -5px rgba(0, 0, 0, 0.1), 0 6px 20px -6px rgba(0, 0, 0, 0.08);
    transition: opacity 0.2s ease-out, transform 0.2s ease-out; /* For open/close animation if desired */
  }

  .notification-item {
    border-radius: 10px; /* Rounded items */
    transition: background-color 0.2s ease;
    border-left: 3px solid transparent; /* Base for alignment */
  }

  .notification-item-unread {
    background-color: rgba(45, 212, 191, 0.12); /* Light teal bg for unread */
    border-left-color: ${tealGradientStart}; /* Teal accent */
  }
  .notification-item-unread .notification-text-content {
    font-weight: 500; /* Radix medium weight for unread text */
    color: var(--gray-12); /* Darker text for emphasis */
  }

  .notification-item-read {
    background-color: rgba(248, 250, 252, 0.5); /* Very subtle light gray */
  }
  .notification-item-read .notification-text-content {
    font-weight: 400; /* Radix regular weight */
    color: var(--gray-11); /* Slightly lighter text for read */
  }
  
  .notification-item:hover {
    background-color: rgba(0, 0, 0, 0.04); /* Subtle hover for all items */
  }

  /* Scrollbar utility (if not globally available) */
  .notification-scrollbar-hide {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .notification-scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

const NotificationDropdown = ({ notifications, isOpen, onClose, showGap = true }) => {
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          !event.target.closest('[data-notification-bell="true"]')) { // Assumes bell has this data attribute
        onClose && onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;

  const gapSize = showGap ? '4px' : '0';

  return (
    <>
      <style>{notificationStyles}</style>
      <Box
        ref={dropdownRef}
        className="absolute w-80 z-50 max-h-96 overflow-y-auto notification-dropdown-container notification-scrollbar-hide"
        // Removed Tailwind classes for bg, shadow, border, rounded from className
        // Retained: absolute, w-80 (320px), z-50, max-h-96 (384px), overflow-y-auto
        style={{ 
          right: '10px', // Adjusted for potentially wider profile area or more generic positioning
          top: `calc(60px + ${gapSize})`, // Adjusted, assuming a slightly shorter header or closer placement
          // Inline styles from className take precedence, but custom classes are better
        }}
      >
        <Flex direction="column" p="3" gap="3">
          <Heading size="4" weight="bold" style={{ color: tealGradientEnd }}>
            Notifications
          </Heading>
          <Separator size="4" style={{ backgroundColor: 'rgba(0,0,0,0.08)' }}/>
          
          {notifications.length === 0 ? (
            <Flex align="center" justify="center" py="5">
              <Text size="2" color="gray">No new notifications</Text>
            </Flex>
          ) : (
            <Flex direction="column" gap="2" width="100%">
              {notifications.map((notification, index) => (
                <Box
                  key={index}
                  p="2" // Padding inside each item
                  className={`notification-item ${notification.isRead ? 'notification-item-read' : 'notification-item-unread'}`}
                >
                  <Text 
                    size="2" 
                    className="notification-text-content" // Class to control weight/color
                    display="block" 
                    mb="1"
                  >
                    {notification.notificationText}
                  </Text>
                  <Flex justify="end">
                    <Text size="1" color="gray">
                      {formatRelativeTime(notification.time)}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </Flex>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default NotificationDropdown;
